import { createApp, type App as VueApp } from 'vue';
import '@/utilities/moment-locales';
import App from './App.vue';
import router from './router';
import store from './store';
import { i18n } from './utilities/i18n';
import Vue3Toastify, { toast } from 'vue3-toastify';
import $rq from './api/api';
import vuetify from './plugins/vuetify';
import { createGtag } from 'vue-gtag';
import './utilities/cookies';
import './public/styles/main.scss';
import AQButton from './components/shared/forms/inputs/AQButton.vue';
import AQTextField from './components/shared/forms/inputs/AQTextField.vue';
import AQCheckBox from './components/shared/forms/inputs/AQCheckBox.vue';
import AQAutocomplete from './components/shared/forms/inputs/AQAutocomplete.vue';
import AQTextArea from './components/shared/forms/inputs/AQTextArea.vue';
import AQSelect from './components/shared/forms/inputs/AQSelect.vue';
import AQModal from './components/shared/containers/AQModal.vue';
import AQIcon from './components/shared/interactions/AQIcon.vue';
import AQToggle from './components/shared/forms/inputs/AQToggle.vue';
import BaseSidebar from './components/navigation/BaseSidebar.vue';
import Navbar from './components/navigation/Navbar.vue';
import { setupModalService } from './components/services/ModalService';
import AQBoxWithTitle from './components/shared/containers/AQBoxWithTitle.vue';
import AQDatepicker from './components/shared/forms/inputs/AQDatepicker.vue';
import { provideEventBus } from '@/utilities/event-bus';
import { resolveCrmBaseUrl } from '@/utilities/utils';
import initValidation from '@/validation';
import 'vue-loading-overlay/dist/css/index.css';
import { setCrmHttpClient, crmMessages } from '@/crm/src';

const app = createApp(App);
// Add i18n instance to the app
app.use(i18n);
app.use(store);
app.use(router);
app.use(vuetify);
app.use(
    createGtag({
        tagId: process.env.VUE_APP_GA_TAG_ID,
    }),
);

app.use(Vue3Toastify, {
    autoClose: 3000,
    useHandler: (toastApp: VueApp) => toastApp.use(vuetify),
});

// Initialize modal service
setupModalService(app);

// register components
app.component('AQButton', AQButton);
app.component('AQTextField', AQTextField);
app.component('AQCheckBox', AQCheckBox);
app.component('AQAutocomplete', AQAutocomplete);
app.component('AQTextArea', AQTextArea);
app.component('AQSelect', AQSelect);
app.component('AQModal', AQModal);
app.component('AQIcon', AQIcon);
app.component('AQToggle', AQToggle);
app.component('BaseSidebar', BaseSidebar);
app.component('Navbar', Navbar);
app.component('AQBoxWithTitle', AQBoxWithTitle);
app.component('AQDatepicker', AQDatepicker);

initValidation();
provideEventBus();
// Wait for vuex-persist to restore the state
store.restored?.then(async () => {
    // Initialize API first
    $rq.init();

    // Wire the CRM submodule's HTTP client to the shell's authenticated axios instance so it
    // inherits the token interceptor and 401-refresh/retry queue. Must run after $rq.init() —
    // CRM must not touch the API at module-eval time.
    setCrmHttpClient(
        $rq.authClient,
        resolveCrmBaseUrl(process.env.VUE_APP_API_URL, process.env.VUE_APP_CRM_API_URL),
    );

    // Merge the CRM submodule's locale bundles into the shell's i18n instance. This is the
    // codebase's first `mergeLocaleMessage` call.
    i18n.global.mergeLocaleMessage('en', crmMessages.en);
    i18n.global.mergeLocaleMessage('fr', crmMessages.fr);

    // Restore language from cookie or user state after store is restored
    const savedLang = window.$cookies?.get('lang') || store.getters.user?.lang || 'en';
    if (savedLang && savedLang !== i18n.global.locale.value) {
        i18n.global.locale.value = savedLang;
        vuetify.locale.current.value = savedLang;
        $rq.setAxiosHeaders({
            'X-AQ-Locale-Identifier': savedLang,
        });
    }

    // Restore auth state from cookies before mounting the app
    // This ensures isLoggedIn is set correctly before router guards run
    await store.dispatch('checkAuthOnAppInit');
    await router.isReady();

    // Restore language to auth store after checkAuthOnAppIni
    if (savedLang && store.getters.user?.lang !== savedLang) {
        store.dispatch('setLang', savedLang);
    }

    // Make $i18n globally available
    app.config.globalProperties.$t = i18n.global.t;
    app.config.globalProperties.$toaster = toast;

    app.mount('#app');
});

export { app };
